/* eslint-disable camelcase */
/**
 * Basic Sensor model.
 *
 */

import moment from "moment";
import { gettext } from "../adapter";
import each from "lodash/each";
import pluck from "lodash/map";
import without from "lodash/without";
import indexOf from "lodash/indexOf";

class SensorModel {
  constructor(props) {
    // Load the properties
    this.id = props.id;
    this.product = props.product;
    this.name = props.name;
    this.installation = props.installation;
    this.owner = props.owner;
    this.role = props.role;
    this.group = props.group;
    this.soilMoisture = props.soilMoisture;
    this.refillPoint = props.refillPoint;
    this.fieldCapacity = props.fieldCapacity;
    this.type = props.type;
    this.enabled = props.enabled;
    this.showPrecipitation = props.showPrecipitation;
    this.showTemperature = props.showTemperature;
    this.warningPercentage = props.warningPercentage;
    this.latestMeasurement = props.latestMeasurement;
    this.csq = props.csq;
    this.active = props.active;
    this.serialNumber = props.serialNumber;
    this.voltage = props.voltage;
    this.battery = props.battery;
    this.rssi = props.rssi;
    this.latitude = props.latitude.toFixed(5);
    this.longitude = props.longitude.toFixed(5);
    this.refillCapacity = 0;
    this.refill24 = 0;
    this.refill48 = 0;
    this.viewSettingsId = props.viewSettingsId;
    this.sensors = props.sensors;
    this.turned_off = false;
    var name = props.advice_region;
    if (name) {
      var sub = name.substring(0, 3);
      if (sub === "MC ") {
        name = name.substring(3);
      }
      this.advice_region = gettext("Soil moisture forecast:") + " " + name;
    } else {
      this.advice_region = "";
    }

    // FC should always be one higher than RP
    if (this.fieldCapacity - this.refillPoint < 1) {
      this.fieldCapacity = this.refillPoint + 1;
    }

    this.startDate = null;
    if (props.activePeriodStart) {
      this.startDate = moment(props.activePeriodStart, "X");
    }

    this.endDate = null;
    if (props.activePeriodEnd) {
      this.endDate = moment(props.activePeriodEnd, "X");
    }

    // Derived values
    // @TODO: We should also have states for outside and inside active period

    this.status = "off";

    // How to determine if new?
    if (!this.startDate) {
      this.status = "new";
    } else if (this.enabled && this.active) {
      this.status = "on";
    } else if (this.enabled && !this.active) {
      this.status = "onNoData";
    } else if (!this.enabled && this.active) {
      this.status = "offData";
    }

    // categorise the measurement period
    var now = moment();

    this.period = "none";
    if (this.startDate && this.startDate.diff(now) > 0) {
      this.period = "now";
    } else if (
      this.endDate &&
      this.endDate.diff(now) < 0 &&
      this.startDate.year() === now.year() - 1
    ) {
      this.period = "last_year";
    } else if (this.endDate && this.endDate.diff(now) < 0) {
      this.period = "past";
    } else if (this.startDate) {
      this.period = "now";
    }

    if (props.soilType) {
      this.soilType = props.soilType;
    } else {
      this.soilType = "Unknown";
    }

    // Measurments and depths
    this.allmeasurements = []; // moet all measurements worden
    this.measurements = {};
    this.depths = {};
    this.advice = {};
    this.precipitationSumList = {};

    // Error object
    this.errors = {
      name: false,
      latitude: false,
      longitude: false,
      startDate: false,
      depths: false,
      warningPercentage: false,
      scale: {},
    };

    // Don't trigger validation directly
    this.valid = true;
  }
}

SensorModel.prototype.setDepths = function (depths) {
  var _this = this;
  this.depths = depths;

  // Default scale
  each(this.depths, function (values, depth) {
    _this.errors.scale[depth] = false;
  });

  // @TODO the errorificatie moet hier nog even weg
};

// Return the activated depths of a sensor
SensorModel.prototype.getVisibleDepths = function () {
  var visibleDepths = [];
  each(this.depths, function (values, key) {
    if (values.visible) {
      visibleDepths.push(key);
    }
  });

  return visibleDepths;
};

SensorModel.prototype.calculateSoilMoisture = function () {
  var _this = this;
  var soilMoisture = 0;
  var fieldCapacity = 0;
  var absoluteSoilMoisture = 0;
  var hasMeasurements = true;

  each(this.depths, function (values, key) {
    // Only count the soil moisture of the visisble depths
    if (values.visible) {
      var depth = _this.depths[key];

      var latestDepthMeasurement = _this.latestMeasurement[key];

      if (latestDepthMeasurement) {
        // Calculate moisture with changed refillPoint
        if (
          depth.originalFieldCapacity !== depth.fieldCapacity ||
          depth.originalRefillPoint !== depth.refillPoint ||
          depth.originalScale !== depth.scale
        ) {
          var rawValue =
            depth.originalRefillPoint +
            ((depth.originalFieldCapacity - depth.originalRefillPoint) * latestDepthMeasurement) /
              depth.originalScale;
          latestDepthMeasurement =
            (rawValue - depth.refillPoint) /
            ((depth.fieldCapacity - depth.refillPoint) / depth.scale);
        }

        soilMoisture = soilMoisture + Math.max(0, latestDepthMeasurement);
        absoluteSoilMoisture = absoluteSoilMoisture + latestDepthMeasurement;
      } else {
        hasMeasurements = false;
      }

      fieldCapacity = fieldCapacity + parseInt(values.scale);
    }
  });

  this.fieldCapacity = fieldCapacity;
  if (hasMeasurements) {
    this.refillCapacity = Math.max(0, _this.fieldCapacity - absoluteSoilMoisture);
    this.soilMoisture = soilMoisture;
  } else {
    this.refillCapacity = false;
    this.soilMoisture = false;
  }

  // Set the correct sensor status
  this.soilMoistureStatus = "noData";
  if (this.status !== "new" && this.period === "now") {
    this.soilMoistureStatus = "normal";
    if (this.status === "onNoData") {
      this.soilMoistureStatus = "noData";
    } else if (this.fieldCapacity - this.soilMoisture < 0) {
      this.soilMoistureStatus = "aboveFieldCapacity";
    } else if (
      this.warningPercentage &&
      this.soilMoisture <= this.fieldCapacity * ((100 - this.warningPercentage) / 100) &&
      this.soilMoisture > 0
    ) {
      this.soilMoistureStatus = "belowWarningPoint";
    } else if (this.soilMoisture <= 0) {
      this.soilMoistureStatus = "belowRefillPoint";
    }
  }
};

SensorModel.prototype.updateName = function (name) {
  this.name = name;
  this.errors.name = this.validateValue(name, ["required"]);
  if (!this.valid) this.validate();
};

SensorModel.prototype.updateWarningPercentage = function (percentage) {
  this.warningPercentage = parseInt(percentage);
  this.errors.warningPercentage = this.validateValue(percentage, ["percentage"]);
  if (!this.valid) this.validate();
};

// Validate the values of the model
SensorModel.prototype.updateLatitude = function (latitude) {
  this.latitude = latitude;
  this.errors.latitude = this.validateValue(latitude, ["required", "latitude"]);
  if (!this.valid) this.validate();
};

SensorModel.prototype.updateLongitude = function (longitude) {
  this.longitude = longitude;
  this.errors.longitude = this.validateValue(longitude, ["required", "longitude"]);
  if (!this.valid) this.validate();
};

// Validate the values of the model
SensorModel.prototype.updateStartDate = function (date) {
  this.startDate = date;
  this.errors.startDate = this.validateValue(date, ["required"]);
  if (!this.valid) this.validate();
};

// Validate the values of the model
SensorModel.prototype.updateEndDate = function (date) {
  this.endDate = date;
};

// Validate the values of the model
SensorModel.prototype.updateRefill24 = function (refill24) {
  this.refill24 = refill24;
};

SensorModel.prototype.updateTurnedOff = function (turned_off) {
  this.turned_off = turned_off;
};

SensorModel.prototype.updateScale = function (depth, value) {
  this.depths[depth].scale = value;
  //  this.errors.scale[depth] = this.validateValue(value, ['required', 'positiveInteger']);
  this.errors.scale[depth] = this.validateValue(value, ["required", "positiveFloat"]);
  if (!this.valid) {
    this.validate();
  }

  // Only execute if we have no errors
  if (!this.errors.scale[depth]) {
    this.calculateSoilMoisture();
  }
};

// Update all scales
SensorModel.prototype.updateScales = function (value) {
  each(this.depths, function (values) {
    values.scale = value;
  });

  this.calculateSoilMoisture();
};

SensorModel.prototype.updateFieldCapacity = function (depth, relativeValue, value) {
  this.depths[depth].relativeFieldCapacity = relativeValue;
  this.depths[depth].fieldCapacity = value;
  this.calculateSoilMoisture();
};

SensorModel.prototype.updateRefillPoint = function (depth, relativeValue, value) {
  this.depths[depth].relativeRefillPoint = relativeValue;
  this.depths[depth].refillPoint = value;
  this.calculateSoilMoisture();
};

SensorModel.prototype.updateDepthVisibility = function (depth, value) {
  this.depths[depth].visible = value;
  this.errors.depths = this.validateValue(pluck(this.depths, "visible"), ["oneTrue"]);
  if (!this.valid) this.validate();
};

// Validate value against rules
SensorModel.prototype.validateValue = function (value, rules) {
  if (indexOf(rules, "required") !== -1) {
    if (String(value).trim() === "" || value == null) {
      return "required";
    }
  }

  // At least one value should be true
  if (indexOf(rules, "oneTrue") !== -1) {
    if (indexOf(value, true) === -1) {
      return "required";
    }
  }

  if (indexOf(rules, "latitude") !== -1) {
    if (!value.match(/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/)) {
      return "invalid";
    }
  }

  if (indexOf(rules, "longitude") !== -1) {
    if (!value.match(/^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/)) {
      return "invalid";
    }
  }

  if (indexOf(rules, "positiveInteger") !== -1) {
    if (!String(value).match(/^\+?(0|[1-9]\d*)$/)) {
      return "invalid";
    }
  }

  if (indexOf(rules, "positiveFloat") !== -1) {
    if (!String(value).match(/^(?:[1-9]\d*|0)?(?:\.\d+)?$/)) {
      // ^(?:[1-9]\d*|0)?(?:\.\d+)?$
      return "invalid";
    }
  }

  if (indexOf(rules, "percentage") !== -1) {
    // Value might be empty *
    if (!String(value).match(/^([1-9]|[1-9][0-9])$|^NaN|null$/)) {
      return "invalid";
    }
  }

  return false;
};

// Return the update functions to validate the values
SensorModel.prototype.validate = function () {
  var _this = this;
  var valid = [];

  this.errors.name = this.validateValue(this.name, ["required"]);
  valid.push(this.errors.name);
  this.errors.startDate = this.validateValue(this.startDate, ["required"]);
  valid.push(this.errors.startDate);
  this.errors.longitude = this.validateValue(this.longitude, ["required", "longitude"]);
  valid.push(this.errors.latitude);
  this.errors.longitude = this.validateValue(this.longitude, ["required", "longitude"]);
  valid.push(this.errors.longitude);
  this.errors.depths = this.validateValue(pluck(this.depths, "visible"), ["oneTrue"]);
  valid.push(this.errors.depths);
  this.errors.warningPercentage = this.validateValue(this.warningPercentage, ["percentage"]);
  valid.push(this.errors.warningPercentage);

  each(this.depths, function (values, depth) {
    _this.errors.scale[depth] = _this.validateValue(_this.depths[depth].scale, [
      "required",
      "positiveFloat",
    ]);
    valid.push(_this.errors.scale[depth]);
  });

  if (without(valid, false).length === 0) {
    _this.valid = true;
    return true;
  } else {
    _this.valid = false;
    return false;
  }
};

export default SensorModel;
